import React, { useState, useEffect } from 'react';
import { ContactTable } from '../components/ContactTable';
import { getAllCampaigns, list } from '../api/api';
import { getAssignmentsForUser, getAssignments, setAssignment } from '../common/assignments';
import { listCognitoUsers } from '../api/users';
import ConfirmationModal from '../components/ConfirmationModal';
import { payloadFromToken } from '../api/auth';

const CallAgents = ({ setAlert, username }) => {
  const [assignmentsState, setAssignmentsState] = useState(getAssignments());
  const [users, setUsers] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [selectedCampaign, setSelectedCampaign] = useState("None");
  const [assessed, setAssessed] = useState("Assessed")
  const [isTransitioning, setIsTransitioning] = useState(false);
  const userToken = localStorage.getItem("id_token");
  const parsedToken = payloadFromToken(userToken);
  const cognitoEmail = parsedToken["email"] || "Unknown";
  const cognitoGrp = parsedToken["cognito:groups"] || "Unknown";
  console.log("PARSED: ", cognitoGrp)
  console.log("Cog email : ", cognitoEmail)

  // Load more pagination state
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [startKey, setStartKey] = useState(null);
  const [campaignAssignedTo, setCampaignAssignedTo] = useState(null);
  const [error, setError] = useState(null);

  const [confirmation, setConfirmation] = useState({
    open: false,
    title: '',
    message: ''
  });

  useEffect(() => {
    getAllCampaigns()
      .then((data) => setCampaigns(data || []))
      .catch((err) => console.error('Failed to load campaigns', err));
  }, []);

  // Load calls when campaign changes
  useEffect(() => {
    const loadCalls = async () => {
      

      setIsLoading(true);
      setError(null);

      try {

        const params = {
          count: window.pcaSettings?.api?.pageSize || 25,
          username: username
        };

        if (cognitoGrp == "CallAgent") {
          params.callAgent = true;
        }
        // Only add campaign if it's not "None"
        if (selectedCampaign !== "None") {
          params.campaign = selectedCampaign;
        }
        console.log('PARAMS', params);
        const response = await list(params);
        console.log('Loaded calls', response);
        setData(response);
        setStartKey(response.StartKey || null);
        setCampaignAssignedTo(response.campaignAssignedTo || null);
      } catch (err) {
        console.error('Failed to load calls', err);
        setError(err);
      } finally {
        setIsLoading(false);
      }
    };

    loadCalls();
  }, [selectedCampaign, username, assessed]);

  // Load more calls function
  const loadMore = async () => {
    if (!startKey || isLoadingMore) return;

    setIsLoadingMore(true);

    try {
      const params = {
        count: window.pcaSettings?.api?.pageSize || 25,
        username: username,
        startKey: startKey,
        campaignAssignedTo: campaignAssignedTo
      };

      // Only add campaign if it's not "None"
      if (selectedCampaign !== "None") {
        params.campaign = selectedCampaign;
      }

      const response = await list(params);

      // Append new records to existing data
      setData(prev => ({
        ...response,
        Records: [...(prev?.Records || []), ...(response.Records || [])]
      }));

      setStartKey(response.StartKey || null);
      setCampaignAssignedTo(response.campaignAssignedTo || null);
    } catch (err) {
      console.error('Failed to load more calls', err);
      setAlert({
        variant: 'error',
        heading: 'Error',
        text: 'Failed to load more calls.'
      });
    } finally {
      setIsLoadingMore(false);
    }
  };

  const handleAssign = (item, user) => {
    try {
      setAssignment(item.key || item.filename, user);
      setAssignmentsState(getAssignments());
      setConfirmation({
        open: true,
        title: 'Assignment Updated',
        message: user
          ? `Agent ${user} assigned successfully.`
          : 'Assignment removed.'
      });
    } catch (e) {
      setConfirmation({
        open: true,
        title: 'Error',
        message: 'Failed to assign agent.'
      });
    }
  };

  const handleCampaignChange = (campaignValue) => {
    setIsTransitioning(true);
    setTimeout(() => {
      setSelectedCampaign(campaignValue);
      setIsTransitioning(false);
    }, 150);
  };

//   const handleAssessedChange = (assessedValue) => {
//     setIsTransitioning(true);
//     setTimeout(() => {
//       setAssessed(assessedValue);
//       setIsTransitioning(false);
//     }, 150);
//   }

  useEffect(() => {
    if (error) {
      setAlert({ variant: 'error', heading: 'Error', text: 'Failed to load calls.' });
    }
  }, [error, setAlert]);

  // Group calls by some identifier (you can modify this based on your data structure)
  const groupedCalls = React.useMemo(() => {
    if (!data?.Records) return [];
    return data.Records.filter(call => call.assessed === true);
  }, [data]);

  // Show transition loading overlay
  if (isTransitioning) {
    return (
      <div className="space-y-6">
        <h1 className="text-2xl font-bold text-gray-900">Assigned to Me</h1>
        
        <div className="flex flex-col items-center justify-center py-16 space-y-4">
          <div className="relative">
            <div className="w-10 h-10 border-3 border-blue-200 border-t-blue-600 rounded-full animate-spin"></div>
          </div>
          <div className="text-center">
            <div className="text-lg font-medium text-gray-700">Loading campaign data...</div>
            <div className="text-sm text-gray-500 mt-1">Please wait a moment</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-gray-900">Assigned to Me</h1>
      
      <div>
        <select 
          className="border border-gray-300 rounded-md px-3 py-2"
          value={selectedCampaign}
          onChange={(e) => handleCampaignChange(e.target.value)}
        >
          <option value="None">All Campaigns</option>
          {campaigns.campaigns?.map(campaign => (
            <option key={campaign.campaignName} value={campaign.campaignName}>
              {campaign.campaignName}
            </option>
          ))}
        </select>
      </div>

      {isLoading ? (
        <div className="flex flex-col items-center justify-center py-12 space-y-4">
          <div className="relative">
            <div className="w-12 h-12 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin"></div>
          </div>
          <div className="text-center">
            <div className="text-lg font-medium text-gray-700">Loading calls...</div>
            <div className="text-sm text-gray-500 mt-1">Fetching your assigned data</div>
          </div>
        </div>
      ) : (
        <ContactTable
          email={cognitoEmail}
          group={cognitoGrp}
          data={groupedCalls || []}
          loading={isLoading}
          assignments={assignmentsState}
          users={users}
          onAssign={handleAssign}
          empty={<div className="text-center py-8 text-gray-500">No assigned calls found for this campaign.</div>}
          onRowClick={(item) => window.open(`/dashboard/${item.key || item.filename}`, '_blank', 'noopener,noreferrer')}
          onLoadMore={loadMore}
          hasMore={!!startKey}
          isLoadingMore={isLoadingMore}
        />
      )}

      <ConfirmationModal
        open={confirmation.open}
        title={confirmation.title}
        message={confirmation.message}
        onClose={() => setConfirmation({ ...confirmation, open: false })}
      />
    </div>
  );
};

export default CallAgents;